package it.unibo.tuprolog.solve.stdlib.primitive

import it.unibo.tuprolog.core.Rule
import it.unibo.tuprolog.core.Substitution
import it.unibo.tuprolog.core.Term
import it.unibo.tuprolog.solve.ExecutionContext
import it.unibo.tuprolog.solve.exception.error.PermissionError
import it.unibo.tuprolog.solve.exception.error.PermissionError.Operation.ACCESS
import it.unibo.tuprolog.solve.exception.error.PermissionError.Permission.PRIVATE_PROCEDURE
import it.unibo.tuprolog.solve.extractSignature
import it.unibo.tuprolog.solve.primitive.BinaryRelation
import it.unibo.tuprolog.solve.primitive.Solve
import it.unibo.tuprolog.utils.buffered

object Clause : BinaryRelation.WithoutSideEffects<ExecutionContext>("clause") {
    override val help: String =
        """
        `clause(+Head, ?Body)`
        
        Enumerates clauses from the static and dynamic knowledge bases whose head unifies with `Head`, unifying `Body` with the corresponding rule body. `Head` must be instantiated and callable; protected library procedures cannot be inspected.

        **Examples**

        ```prolog
        ?- assertz((p(X) :- q(X), r(X))).
        yes.

        ?- clause(p(1), B).
        B = (q(1), r(1)).

        ?- clause(X, true).
        throws error(instantiation_error, _).

        ?- clause(atom(_), B).
        throws error(permission_error(access, private_procedure, atom/1), _).
        ```
        """.trimIndent()

    override fun Solve.Request<ExecutionContext>.computeAllSubstitutions(
        first: Term,
        second: Term,
    ): Sequence<Substitution> {
        ensuringArgumentIsInstantiated(0)
        ensuringArgumentIsStruct(0)
        if (!second.isVar) {
            ensuringArgumentIsCallable(1)
        }
        val head = first.castToStruct()
        val headSignature = head.extractSignature()
        if (context.libraries.hasProtected(headSignature)) {
            throw PermissionError.of(context, signature, ACCESS, PRIVATE_PROCEDURE, headSignature.toIndicator())
        }
        val template = Rule.of(head, second)
        return (context.staticKb[head] + context.dynamicKb[head].buffered())
            .map {
                mgu(it, template)
            }.filter {
                it.isSuccess
            }
    }
}
